/**
 * useHeatCyclingBatches Hook
 *
 * Fetches substrate batches currently in heat cycling (sterilisation) or cooling.
 * Used by the Heat Cycling operational dashboard.
 */

import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/supabase-client'

/**
 * Call production-operations Edge Function
 * @param {string} action - The action to perform
 * @param {Object} data - Request payload
 * @returns {Promise<Object>} Response data
 */
async function callProductionOperations(action, data = {}) {
  const { data: responseData, error } = await supabase.functions.invoke('production-operations', {
    body: { action, ...data }
  })

  if (error) {
    throw new Error(error.message || 'Edge function error')
  }

  if (responseData?.error) {
    throw new Error(responseData.message || responseData.error)
  }

  return responseData
}

/**
 * Hook to fetch substrate batches in HEAT_CYCLING or COOLING status
 * @returns {Object} { batches, summary, loading, error, refetch }
 */
export function useHeatCyclingBatches() {
  const [batches, setBatches] = useState([])
  const [summary, setSummary] = useState({ heating: 0, cooling: 0, ready_to_use: 0 })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchBatches = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const response = await callProductionOperations('heat_cycling_list')
      setBatches(response.data || [])
      setSummary(response.summary || { heating: 0, cooling: 0, ready_to_use: 0 })
    } catch (err) {
      console.error('[useHeatCyclingBatches] Error:', err)
      setError(err.message)
      setBatches([])
      setSummary({ heating: 0, cooling: 0, ready_to_use: 0 })
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchBatches()
  }, [fetchBatches])

  return {
    batches,
    summary,
    loading,
    error,
    refetch: fetchBatches
  }
}

/**
 * Hook for heat cycling mutations
 * @returns {Object} { markAsReady, loading, error }
 */
export function useHeatCyclingMutations() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  /**
   * Mark a substrate batch as ready (COOLING -> READY)
   * @param {string} substrateBatchId - Substrate batch UUID
   * @returns {Promise<Object>} Updated batch
   */
  const markAsReady = useCallback(async (substrateBatchId) => {
    setLoading(true)
    setError(null)

    try {
      const response = await callProductionOperations('mark_cooled', {
        substrate_batch_id: substrateBatchId
      })
      return response.data
    } catch (err) {
      console.error('[useHeatCyclingMutations.markAsReady] Error:', err)
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  return {
    markAsReady,
    loading,
    error
  }
}

export default useHeatCyclingBatches
